import {
  Badge,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  RefreshButton,
  Select,
  Table,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAutoAwesome, MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  AgentJobCard,
  agentJobsService,
  AGENT_JOB_STATUSES,
  AGENT_JOB_TRIGGERS,
} from "services/agentJobsService";
import { getThemeColor } from "theme/colors";

const STATUS_COLOR: Record<string, string> = {
  Completed: "green",
  Failed: "red",
  Cancelled: "orange",
  Running: "blue",
  WaitingForApproval: "red",
  WaitingForInput: "purple",
  Planned: "gray",
  Paused: "gray",
};

/** Read-only tenant view of the company's headless runs — webhook runs, scheduled and portal-run workflows (WP-42). Chat turns are not listed. */
const AgentJobs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<AgentJobCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [triggerFilter, setTriggerFilter] = useState<string>("all");
  const pageSize = 20;

  useEffect(() => {
    loadJobs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, triggerFilter]);

  const loadJobs = async (pageToLoad: number) => {
    try {
      setLoading(true);
      const data = await agentJobsService.list(
        pageToLoad,
        pageSize,
        statusFilter === "all" ? undefined : statusFilter,
        triggerFilter === "all" ? undefined : triggerFilter,
      );
      setJobs(data.items);
      setTotal(data.total);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.agentJobs.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Flex
      direction="column"
      w="full"
      maxW="100%"
      overflowX="hidden"
      gap={5}
      minW={0}
    >
      <Flex justify="space-between" align="center">
        <HStack>
          <MdAutoAwesome size={32} color={getThemeColor("icon").base} />
          <Heading
            size="2xl"
            fontWeight="bold"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.agentJobs.title")}
          </Heading>
        </HStack>
        <RefreshButton onClick={() => loadJobs(page)} loading={loading} />
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text>{error}</Text>
        </InfoBox>
      )}

      <Card.Root>
        <Card.Body>
          <HStack gap={3}>
            <Select
              value={[statusFilter]}
              onValueChange={(details) => {
                setStatusFilter(details.value[0] || "all");
                setPage(1);
              }}
              items={[
                { label: t("app.agentJobs.allStatuses"), value: "all" },
                { label: t("app.agentJobs.needsYou"), value: "needsYou" },
                ...AGENT_JOB_STATUSES.map((s) => ({
                  label: t(`app.agentJobs.status.${s}`),
                  value: s,
                })),
              ]}
              w="240px"
            />
            <Select
              value={[triggerFilter]}
              onValueChange={(details) => {
                setTriggerFilter(details.value[0] || "all");
                setPage(1);
              }}
              items={[
                { label: t("app.agentJobs.allTriggers"), value: "all" },
                ...AGENT_JOB_TRIGGERS.map((tr) => ({
                  label: t(`app.agentJobs.trigger.${tr}`),
                  value: tr,
                })),
              ]}
              w="200px"
            />
          </HStack>
        </Card.Body>
      </Card.Root>

      <Card.Root w="full" maxW="100%">
        <Card.Body
          p={0}
          overflowX="auto"
          overflowY="hidden"
          w="full"
          maxW="100%"
          minW={0}
        >
          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Text color={{ base: "gray.600", _dark: "white" }}>
                {t("app.common.loading")}
              </Text>
            </Flex>
          ) : jobs.length === 0 ? (
            <Flex justify="center" align="center" minH="200px">
              <Text color={{ base: "gray.600", _dark: "white" }}>
                {t("app.agentJobs.noJobs")}
              </Text>
            </Flex>
          ) : (
            <Table.Root variant="outline" size="sm" minW="900px" w="full">
              <Table.Header>
                <Table.Row bg={{ base: "gray.50", _dark: "gray.800" }}>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.statusHeader")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.titleHeader")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.triggerHeader")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.owner")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.pending")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.tokens")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.cost")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.agentJobs.createdAt")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.common.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {jobs.map((job) => (
                  <Table.Row
                    key={job.id}
                    cursor="pointer"
                    _hover={{ bg: { base: "gray.50", _dark: "gray.700" } }}
                    onClick={() => navigate(`/admin/agent-jobs/${job.id}`)}
                  >
                    <Table.Cell>
                      <Badge
                        colorPalette={STATUS_COLOR[job.status] ?? "gray"}
                        variant="subtle"
                      >
                        {t(`app.agentJobs.status.${job.status}`)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell maxW="360px">
                      <Text fontWeight="medium" truncate>
                        {job.title}
                      </Text>
                      {job.summary && (
                        <Text
                          fontSize="xs"
                          color={{ base: "gray.500", _dark: "gray.400" }}
                          truncate
                        >
                          {job.summary}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {t(`app.agentJobs.trigger.${job.trigger}`)}
                      </Text>
                      {job.sourceKind !== null && (
                        <Text
                          fontSize="xs"
                          color={{ base: "gray.500", _dark: "gray.400" }}
                        >
                          {t(`app.agentJobs.source.${job.sourceKind}`, {
                            defaultValue: "",
                          })}
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="xs" fontFamily="mono">
                        {job.ownerCustomerId ?? "—"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {job.pendingApprovals.length > 0
                        ? t("app.agentJobs.pendingApprovals", {
                            count: job.pendingApprovals.length,
                          })
                        : job.questions.length > 0
                          ? t("app.agentJobs.pendingQuestion")
                          : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      {job.inputTokens + job.outputTokens > 0
                        ? (job.inputTokens + job.outputTokens).toLocaleString()
                        : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      {job.costUsd > 0 ? `$${job.costUsd.toFixed(4)}` : "—"}
                    </Table.Cell>
                    <Table.Cell>{formatDate(job.createdAt)}</Table.Cell>
                    <Table.Cell>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/agent-jobs/${job.id}`);
                        }}
                        size="sm"
                        variant="ghost"
                        colorPalette="blue"
                        title={t("app.agentJobs.viewDetails")}
                      >
                        <MdVisibility />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Card.Body>
      </Card.Root>

      {total > 0 && (
        <HStack justify="space-between">
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.common.showing")} {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, total)} {t("app.common.of")} {total}
          </Text>
          <HStack>
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              size="sm"
            >
              {t("app.common.previous")}
            </Button>
            <Text fontSize="sm">
              {t("app.common.page")} {page} {t("app.common.of")} {totalPages}
            </Text>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              size="sm"
            >
              {t("app.common.next")}
            </Button>
          </HStack>
        </HStack>
      )}
    </Flex>
  );
};

export default AgentJobs;
